<template>
  <app-layout :heading="heading">
    <v-progress-linear
      v-if="isLoading"
      :indeterminate="true"/>

    <timesheet-created-not-saved-warning
      v-if="validTimesheets.length"
      :value="true"
    />

    <v-layout>
      <v-flex>
        <v-alert
          v-if="batch.length > 0 && validTimesheets.length === 0"
          :value="true"
          type="error"
        >{{ $t('noValidTimesheets') }}
        </v-alert>
        <v-alert
          v-else-if="invalidTimesheets.length > 0"
          :value="true"
          class="mb-3"
          type="warning"
          v-html="$t('removedEmployees', {names: invalidTimesheets.map(el => el.name || el.month).join(', ')})"/>

      </v-flex>

    </v-layout>

    <v-card>
      <confirm-dialog
        :is-open="isSaveDialogOpen"
        :dialog-header="$t('timesheetSaveHeader')"
        :dialog-body="$t('timesheetSaveBody')"
        :is-loading="isLoading"
        :is-disabled="!!queue"
        :confirm-button-text="!!queue ? `${$t('generating')} ${queue.progress }%` : $t('download')"
        @confirm="saveSchemas"
        @dialogToggle="val => isSaveDialogOpen = val"
      />
      <v-expansion-panel
        class="elevation-0"
        light>
        <v-expansion-panel-content
          v-for="(item, index) in validTimesheets"
          :key="index"
          @click.native="openedBatchIndex(index)"
        >
          <template slot="header">
            <h3 class="subtitle align-content-center">
              {{ $route.query.month }} {{ item.month || item.name }}
              <v-icon
                v-if="openedRows.includes(index)"
                class="pa-1"
                small
                color="success">check_circle
              </v-icon>
              <v-chip v-if="item.schema.is_part_time_worker">{{ $t('is_part_time_worker') }}</v-chip>
            </h3>
          </template>
          <employee-timesheet-editor
            :batch="{
              days: item.days ,
              schema: item.schema
            }"
          />
        </v-expansion-panel-content>
      </v-expansion-panel>

      <template v-if="validTimesheets.length">
        <v-divider/>
        <v-card-actions>

          <v-spacer/>
          <v-btn
            :loading="isLoading"
            :disabled="isLoading"
            color="primary"
            @click.native="isSaveDialogOpen = true">{{ $t('download') }}
          </v-btn>
        </v-card-actions>

      </template>
    </v-card>
  </app-layout>
</template>

<script>
import TimesheetApi from '@/api/TimesheetApi'
import EmployeeTimesheetEditor from '@/components/routes/employees/timesheets/EmployeeTimesheetEditor'
import ConfirmDialog from '@/components/global-components/confirmDialog/ConfirmDialog'
import TimesheetCreatedNotSavedWarning from '@/components/routes/employees/timesheets/TimesheetCreatedNotSavedWarning'
import CompanyApi from '@/api/CompanyApi'

export default {
  name: 'TimesheetBatchEdit',
  components: { TimesheetCreatedNotSavedWarning, ConfirmDialog, EmployeeTimesheetEditor },
  props: ['companyId'],
  data () {
    return {
      isLoading: false,
      isSaveDialogOpen: false,
      openedRows: [],
      batch: [],
      queue: null,
      queueInterval: null
    }
  },
  computed: {
    heading () {
      if (this.$route.query.archiveName) {
        return this.$route.query.archiveName.split('-').join(' ')
      }
      return this.$t('heading')
    },
    isMultiEmpolyees () {
      return Boolean(this.$route.query.multiEmployees)
    },
    validTimesheets () {
      return this.batch.filter(el => {
        return el.days.some(day => day.is_allowed_to_work)
      })
    },
    invalidTimesheets () {
      return this.batch.filter(el => {
        return this.validTimesheets.includes(el) === false
      })
    }
  },
  methods: {
    async getSchemas () {
      this.isLoading = true

      let { month, schema_id, schemas, months, ignore_last_6_days, lastDayOfCalculation } = this.$route.query

      schemas = schemas && schemas.split(',').map(id => ({ id }))
      months = months && months.split(',').map(month => ({ month }))
      if (typeof ignore_last_6_days === 'string') {
        ignore_last_6_days = ignore_last_6_days === 'true'
      }

      try {
        if (this.isMultiEmpolyees) {
          this.batch = await TimesheetApi.batchMultiEmployees({
            schemas,
            month,
            ignore_last_6_days,
            lastDayOfCalculation
          })
        } else {
          this.batch = await TimesheetApi.batchMultiMonths({
            months,
            schema_id,
            ignore_last_6_days,
            lastDayOfCalculation
          })
        }
      } catch ({ errors }) {
        this.$notification(errors)
      }
      this.isLoading = false
    },
    async saveSchemas () {
      this.isLoading = true
      const { archiveName, schema_id, month } = this.$route.query

      try {
        if (this.isMultiEmpolyees) {
          this.queue = await TimesheetApi.archiveMultiEmployees({ month, schemas: this.validTimesheets })
        } else {
          this.queue = await TimesheetApi.archiveMultiMonths({ schema_id, months: this.validTimesheets })
        }
      } catch (e) {
        this.$notification(e)
        this.isLoading = false
      }
      this.isLoading = false

      this.queueInterval = setInterval(async () => {
        try {
          this.queue = await CompanyApi.getQueue(this.queue.id)
          if (this.queue.is_finished || this.queue.progress > 100) {
            await CompanyApi.downloadQueue(this.queue.id, archiveName || month)
            this.queue = null
            clearInterval(this.queueInterval)
            this.$router.replace({ name: 'employees' })
            this.isSaveDialogOpen = false
          }
        } catch (e) {
          this.$notification(e)
          this.queue = null
          clearInterval(this.queueInterval)
        }
      }, 1000)
    },
    openedBatchIndex (index) {
      if (this.openedRows.includes(index) === false) {
        setTimeout(() => {
          // delay adding it a little bit to make it feel like "checked"
          this.openedRows.push(index)
        }, 1000)
      }
    }
  },
  created () {
    this.getSchemas()
  },
  destroyed () {
    this.queue = null
    clearInterval(this.queueInterval)
  },
  i18n: {
    messages: {
      de: {
        heading: 'Übersicht Stundenzettel',
        download: 'Herunterladen',
        timesheetSaveHeader: 'Bestätigung',
        timesheetSaveBody: 'Stimmen die vorliegenden Daten mit der Realität überein?',
        noValidTimesheets: 'Es konnten keine Monate erzeugt werden in denen gearbeitet werden kann. ',
        removedEmployees: 'Keine Arbeitstage: {names}',
        is_part_time_worker: 'Ohne feste Arbeitszeiten',
        generating: 'Erstellen...'
      },
      en: {
        heading: 'Timesheet overview',
        download: 'Download',
        timesheetSaveHeader: 'Confirm',
        timesheetSaveBody: 'Do the available data correspond to reality?',
        noValidTimesheets: 'No months could be created in which to work. ',
        removedEmployees: 'No workdays: {names}',
        is_part_time_worker: 'Without fixed work hours',
        generating: 'Generating...'
      },
      tr: {
        heading: 'Zaman çizelgesine genel bakış',
        download: 'İndir',
        timesheetSaveHeader: 'Onayla',
        timesheetSaveBody: 'Verinin doğruluğunu onaylıyor musunuz?',
        noValidTimesheets: 'Çalışmak için hiçbir ay yaratılamaz.',
        removedEmployees: 'Çalışma günü yok: {names}',
        is_part_time_worker: 'Sabit çalışma saatleri olmadan',
        generating: 'Yaratma...'
      },
      ru: {
        heading: 'Обзор табеля',
        download: 'Скачать',
        timesheetSaveHeader: 'Подтвердить',
        timesheetSaveBody: 'Соответствуют ли имеющиеся данные действительности?',
        noValidTimesheets: 'Невозможно было создать месяцы, в течение которых можно было бы работать.',
        removedEmployees: 'Рабочих дней нет: {names}',
        is_part_time_worker: 'Без фиксированного рабочего времени',
        generating: 'Генерация...'
      }
    }
  }
}
</script>
